import {createStore} from "zustand"
import {devtools} from "zustand/middleware"
import {immer} from "zustand/middleware/immer"
import type {WritableDraft} from "immer"
import {filterMap} from "../object"
import {DEFAULT_LANG} from "./helpers/getSupportedLocale"

export const LangStorageKey = "PerkboxLangStorage"

/**
 * Abstraction over some async storage.
 * For native can pass in AsyncStorage,
 * For web just wrap localStorage methods in async wrappers
 */
export type Storage = {
  setItem(key: string, value: any): Promise<void>
  getItem(key: string): Promise<string | null>
  removeItem(key: string): Promise<void>
  getAllKeys(callback?: (error?: Error) => void): Promise<string[]>
}

export const BucketStates = {
  /** The initial state of a bucket without any messages */
  missing: "missing",
  /** The initial state of a bucket without bundled messages */
  local: "local",
  /** A bucket with messages from storage (should override local) */
  stored: "stored",
  /**
   * A bucket that may or may not have local messages, but failed to fetch
   * updates. This likely needs improving, but it will unblock the rendering
   */
  failed: "failed",
  /** A bucket with the latest fetched messages, should never be replaced */
  latest: "latest"
} as const

export type BucketState = (typeof BucketStates)[keyof typeof BucketStates]

/** Object of intl key:value pairs as returned from Phrase */
export type Messages = Record<string, string>

export type Bucket = {
  meta: {
    preferRemote: boolean
    fetchCount: number
  }
  ready: boolean
  state: BucketState
  messages: Messages
}

/** Grouped phrase buckets */
export type Buckets = Record<string, Bucket>

export interface LangLoaderInit {
  /**
   * Initial messages to render. If present these will allow the app to render immediately
   * and any new messages will be fetched in the background, overwriting old values as they load.
   */
  initialMessages?: Record<string, Messages>
  /**
   * Storage mechanism to use for persisting phrase keys. This is only intended for
   * use by native because it can push phrase keys into the bundle with any code push.
   * i.e the client bundle won't rely on phrase keys that aren't already in storage
   */
  storage?: Storage
  contentFetcher: (bucket: string, locale: string) => Promise<Messages>
  /**
   * list of bucket names that can have local data, but should try to fetch
   * the latest data before displaying
   */
  favourRemote?: string[]
  /**
   * debounce timer between messages updates to avoid frequent re-renders
   * after a series of promises resolve
   */
  debounceIntervalMs?: number
}

export interface LangLoaderState {
  locale: string
  /**
   * collection of all messages from the store, this should be favoured over
   * aggregating individual buckets
   */
  messages: Messages
  buckets: Buckets
}

type _LangLoaderStore = LangLoaderState & {
  /** All apis the store exposes grouped under a stable key for easy destructuring */
  actions: LangLoaderActions
}

/** All apis the store exposes */
export interface LangLoaderActions {
  /** fetch content via `contentFetcher` */
  getContent: (bucket: string[]) => Promise<void>
  /**
   * [internal] update internal store of messages with debouncing to reduce re-renders
   * append/overwrite only at this time
   */
  _addMessages: (bucket: string) => void
}

export type LangLoaderStore = ReturnType<typeof createLangStore>

export const createLangStore = ({
  initialMessages,
  contentFetcher,
  storage,
  favourRemote = [],
  debounceIntervalMs = 15
}: LangLoaderInit) => {
  /** internal queue for batching message updates */
  let _bucketQueue: string[] = []
  /** internal timer for batching message updates */
  let _timeoutHandle: ReturnType<typeof setTimeout>

  const store = createStore<_LangLoaderStore>()(
    devtools(
      immer((set, get) => {
        const initialState: LangLoaderState = {
          locale: DEFAULT_LANG,
          messages: {},
          buckets: {}
        }

        const _addMessages: LangLoaderActions["_addMessages"] = async (bucket) => {
          _bucketQueue.push(bucket)

          if (_timeoutHandle) clearTimeout(_timeoutHandle)

          _timeoutHandle = setTimeout(() => {
            set(
              (state) => {
                updateMessages(state, _bucketQueue)
                _bucketQueue = []
              },
              undefined,
              "updateBatchedMessages"
            )
          }, debounceIntervalMs)
        }

        const getContent: LangLoaderActions["getContent"] = async (buckets) => {
          const allFetched = await Promise.all(
            buckets.map((bucket) =>
              contentFetcher(bucket, get().locale)
                .then((messages): RetrievedBucketOk => ({state: "ok", bucket, messages}))
                .catch((): RetrievedBucketFail => ({state: "fail", bucket, messages: undefined}))
            )
          )

          const fetched = groupSuccessAndFailures(allFetched)

          if (storage) {
            await Promise.all(
              fetched.withKeys.map(({messages, bucket: bucketKey}) => {
                storage.setItem(`${LangStorageKey}:${bucketKey.toLowerCase()}`, JSON.stringify(messages)).catch(console.error)
              })
            )
          }

          set(
            (state) => {
              updateFetchedBuckets(state, buckets, fetched, favourRemote)
            },
            undefined,
            "setFetchedKeys"
          )
        }

        return {
          ...initialState,
          actions: {
            getContent,
            _addMessages
          }
        }
      }),
      {name: "LangStore"}
    )
  )

  // sync messages with bucket updates
  store.subscribe(({buckets, actions}, previous) => {
    for (const bucketName of Object.keys(buckets)) {
      const bucket = buckets[bucketName]
      const previousState = previous.buckets[bucketName]?.state

      if (bucket?.ready && previousState !== bucket.state) {
        actions._addMessages(bucketName)
      }
    }
  })

  // set initial buckets AFTER subscription to keep messages in sync
  if (initialMessages) {
    const initialBuckets = filterMap(initialMessages, (bucket, messages): [string, Bucket] => {
      const emptyBucket = createEmptyBucket(bucket, favourRemote)
      return [
        bucket,
        {
          ...emptyBucket,
          state: BucketStates.local,
          messages,
          ready: !emptyBucket.meta.preferRemote
        }
      ]
    })

    store.setState({buckets: initialBuckets}, undefined, "setInitialMessages")
  }

  // update buckets from local storage
  if (storage) {
    // NOTE: we don't await here. Instead the local buckets will be fetched in the background and update the store in the background
    getAllStoredBuckets(storage)
      .then((buckets) => Promise.all(buckets.map((b) => getStoredPhraseBucket(storage, b))))
      .then((stored) => stored.filter((b) => b.state === "ok"))
      .then((storedBuckets) =>
        store.setState(
          (state) => {
            storedBuckets.forEach((stored) => {
              state.buckets[stored.bucket] ??= createEmptyBucket(stored.bucket, favourRemote)
              const bucket = assert(state.buckets[stored.bucket], "the bucket should have just been created")

              if ([BucketStates.local, BucketStates.missing, BucketStates.failed].includes(bucket.state)) {
                bucket.state = BucketStates.stored
                bucket.ready = !bucket.meta.preferRemote
                // technically, local messages should never have keys that aren't also in cache, but we merge just in case
                bucket.messages = {...bucket.messages, ...stored.messages}
              }
            })
          },
          undefined,
          "setStoredBuckets"
        )
      )
      .catch((e) => console.warn("failed reading lang keys from storage", e))
  }

  return store
}

type RetrievedBucketOk = Extract<RetrievedBucket, {state: "ok"}>
type RetrievedBucketFail = Extract<RetrievedBucket, {state: "fail"}>
type RetrievedBucket =
  | {
      state: "ok"
      bucket: string
      messages: Messages
    }
  | {
      state: "fail"
      bucket: string
      messages: undefined
    }

async function getAllStoredBuckets(storage: Storage) {
  const keys = await storage.getAllKeys()
  return keys.filter((key) => key.startsWith(LangStorageKey + ":")).map((key) => key.replace(LangStorageKey + ":", ""))
}

async function getStoredPhraseBucket(storage: Storage, bucket: string): Promise<RetrievedBucket> {
  try {
    const stored = await storage.getItem(`${LangStorageKey}:${bucket}`)
    if (!stored) return {state: "fail", bucket, messages: undefined}

    const messages = JSON.parse(stored) as Messages
    if (typeof messages !== "object" || messages === null) throw new Error("invalid phrase value:\n" + stored)

    return {state: "ok", bucket, messages}
  } catch (e) {
    console.warn(`error reading parsing phrase key ${bucket}`)
    console.error(e)
    return {state: "fail", bucket, messages: undefined}
  }
}

interface FetchedBuckets {
  withKeys: RetrievedBucketOk[]
  empty: RetrievedBucketFail[]
}
function groupSuccessAndFailures(fetched: RetrievedBucket[]) {
  return fetched.reduce<FetchedBuckets>(
    ({withKeys, empty}, {bucket, messages}) => {
      if (messages) return {empty, withKeys: withKeys.concat({state: "ok", bucket, messages})}
      return {withKeys, empty: empty.concat({state: "fail", bucket, messages})}
    },
    {withKeys: [], empty: []}
  )
}

function updateMessages(state: WritableDraft<LangLoaderState>, buckets: string[]) {
  buckets
    .map((b) => state.buckets[b])
    .filter(Boolean)
    .forEach((b) => {
      for (const [key, value] of Object.entries(b.messages)) {
        state.messages[key] = value
      }
    })
}

function updateFetchedBuckets(
  state: WritableDraft<LangLoaderState>,
  buckets: string[],
  {empty, withKeys}: FetchedBuckets,
  favourRemote: string[]
) {
  // initialise empty buckets if they haven't been seen
  buckets.forEach((bucket) => {
    state.buckets[bucket] ??= createEmptyBucket(bucket, favourRemote)
  })

  withKeys.forEach(({messages, bucket: bucketKey}) => {
    const bucket = assert(state.buckets[bucketKey])
    bucket.state = BucketStates.latest
    bucket.ready = true
    bucket.messages = messages
  })

  empty.forEach(({bucket: bucketKey}) => {
    const bucket = assert(state.buckets[bucketKey])
    bucket.meta.fetchCount++
    // at this point we just fall back to displaying the bucket however it is
    // if there isn't anything local or fetched, it will display the keys themselves, but at least the journey can continue
    bucket.ready = true
    bucket.state = BucketStates.failed
    //  TODO: attempt to fetch bucket again
    // get().actions.getContent([bucketKey])
  })
}

function createEmptyBucket(bucket: string, favourRemote: string[]): Bucket {
  return {
    messages: {},
    ready: false,
    meta: {
      fetchCount: 0,
      preferRemote: favourRemote.includes(bucket)
    },
    state: BucketStates.missing
  }
}

function assert<A>(maybe: A | undefined, msg?: string): A {
  if (!maybe) throw new Error(msg || `Bucket should be initialised at the beginning of getContent`)
  return maybe as A
}
